import React, { ReactNode, useRef, useState } from 'react';
import {
  TextInput,
  TextInputProps,
} from 'react-native';

import { colors } from '../constants/colors';
import { Close, IconEye, IconEyeInvisible, IconWarning } from '../icons';
import {
  Container,
  ErrorText,
  ErrorWrap,
  HelpText,
  InputWrapper,
  Label,
  PressableIconWrap,
  StyledRequired,
  StyledTextInput,
} from './styles';

type InputTypes = TextInputProps['inputMode'] | 'password';

export type InputProps = {
  type?: InputTypes;
  hasError?: boolean | string;
  label?: string;
  help?: string;
  clearable?: boolean;
  onClear?: () => void;
  disabled?: boolean;
  icon?: ReactNode;
  onIconPress?: () => void;
  isRequired?: boolean;
  id: string;
} & TextInputProps;

export const Input: React.FC<InputProps> = ({
  id,
  type = 'text',
  editable,
  onFocus,
  onBlur,
  secureTextEntry,
  hasError,
  label,
  help,
  clearable,
  onClear,
  icon,
  onIconPress,
  isRequired,
  ...rest
}) => {
  const ref = useRef<TextInput>(null);
  const [secureText, setSecureText] = useState(
    secureTextEntry || type === 'password'
  );
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);

  const handleFocus: TextInputProps['onFocus'] = (event) => {
    setFocus(true);
    onFocus?.(event);
  };

  const handleBlur: TextInputProps['onBlur'] = (event) => {
    setFocus(false);
    onBlur?.(event);
  };

  const renderEye = () => (secureText ? <IconEyeInvisible /> : <IconEye />);

  const isDisabled = editable === false;
  return (
    <Container>
      {label && (
        <Label onPress={() => ref.current?.focus()} id={`inputLabel-${id}`}>
          {label}
          {isRequired && <StyledRequired aria-hidden> *</StyledRequired>}
        </Label>
      )}
      {help && <HelpText>{help}</HelpText>}
      {hasError && typeof hasError === 'string' && (
        <ErrorWrap testID={rest.testID ? `${rest.testID}-error` : undefined}>
          <IconWarning color={colors.red} />
          <ErrorText>{hasError}</ErrorText>
        </ErrorWrap>
      )}
      <InputWrapper>
        <StyledTextInput
          ref={ref}
          id={id}
          {...(label && { 'aria-labelledby': `inputLabel-${id}` })}
          inputMode={type === 'password' ? 'text' : type}
          placeholderTextColor={colors.gray1}
          hover={hover && !focus}
          editable={editable}
          disabled={isDisabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onPointerEnter={() => setHover(true)}
          onPointerLeave={() => setHover(false)}
          hasFocus={focus}
          secureTextEntry={secureText}
          hasError={Boolean(hasError)}
          {...rest}
        />
        {type === 'password' && (
          <PressableIconWrap onPress={() => setSecureText(!secureText)}>
            {renderEye()}
          </PressableIconWrap>
        )}
        {clearable && (
          <PressableIconWrap onPress={onClear}>
            <Close />
          </PressableIconWrap>
        )}
        {icon && (
          <PressableIconWrap onPress={onIconPress}>
            {icon}
          </PressableIconWrap>
        )}
      </InputWrapper>
    </Container>
  );
};
